import { environmentConfig } from '../services/universalApi'
import { GOOGLE_AUTH_MESSAGE_TYPE } from '../constants/googleOAuth'
import { getStoredReferralCode } from '../services/referralService'
import i18n from '../i18n'

const POPUP_TIMEOUT_MS = 5 * 60 * 1000

export type GoogleOAuthPopupOptions = {
  /** Intended account role for new Google sign-ups. Defaults to student. */
  role?: 'student' | 'affiliate'
  /** Skip attaching stored student referral codes (affiliates). */
  skipReferral?: boolean
}

function buildGoogleStartUrl(options: GoogleOAuthPopupOptions = {}): string {
  const redirectUri = `${window.location.origin}/auth/google/callback`
  const role = options.role || 'student'
  const ref = options.skipReferral || role === 'affiliate' ? null : getStoredReferralCode()
  const refQuery = ref ? `&referral_code=${encodeURIComponent(ref)}` : ''
  const roleQuery = role !== 'student' ? `&role=${encodeURIComponent(role)}` : ''
  const locale = (i18n.language || 'en').slice(0, 8)
  const localeQuery = locale ? `&locale=${encodeURIComponent(locale)}` : ''
  return (
    `${environmentConfig.apiBaseUrl}/auth/google/start?redirect_uri=${encodeURIComponent(redirectUri)}` +
    `${refQuery}${roleQuery}${localeQuery}`
  )
}

/**
 * Start Google OAuth. In Vite DEV (localhost), uses same-tab redirect — popups are
 * often blocked and production already allows localhost redirect_uri. GoogleAuthCallback
 * completes the session via completeGoogleSignIn when there is no window.opener.
 */
export function openGoogleOAuthPopup(
  options: GoogleOAuthPopupOptions = {},
): Promise<{ access_token: string }> {
  const startUrl = buildGoogleStartUrl(options)

  // Same-tab redirect is the reliable path for local UI → production API.
  if (import.meta.env.DEV) {
    window.location.assign(startUrl)
    return new Promise(() => {
      /* navigation in progress */
    })
  }

  return new Promise((resolve, reject) => {
    const width = 500
    const height = 650
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2)
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2)

    const popup = window.open(
      startUrl,
      'zimark_google_oauth',
      `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    )

    if (!popup) {
      // Fallback: same-tab redirect (GoogleAuthCallback handles non-popup).
      window.location.assign(startUrl)
      return
    }

    let finished = false

    const cleanup = () => {
      finished = true
      window.clearTimeout(timeoutId)
      window.clearInterval(intervalId)
      window.removeEventListener('message', onMessage)
    }

    const timeoutId = window.setTimeout(() => {
      if (finished) return
      cleanup()
      if (!popup.closed) popup.close()
      reject(new Error('Google sign-in timed out. Please try again.'))
    }, POPUP_TIMEOUT_MS)

    const intervalId = window.setInterval(() => {
      if (finished) return
      if (popup.closed) {
        cleanup()
        reject(new Error('Google sign-in was cancelled.'))
      }
    }, 400)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const data = event.data
      if (!data || data.type !== GOOGLE_AUTH_MESSAGE_TYPE) return

      cleanup()
      if (data.success && data.access_token) {
        // Keep zimark_ref after Google auth:
        // - New students: already attributed via OAuth state; leftover code is harmless
        //   (apply-code rejects duplicates) and still useful if attribution failed.
        // - Existing students: must keep the code so Account Settings → apply can claim it.
        // Email register clears only after successful attribution.
        resolve({ access_token: data.access_token })
        return
      }
      reject(new Error(data.error || 'Google sign-in failed.'))
    }

    window.addEventListener('message', onMessage)
  })
}
